import React from "react";
import DatePickerCustom from "../../component/date-picker/DatePickerCustom";
import { ConfirmDialog } from "../../component/ConfirmDialog";
import { APP_DATE_TIME_FORMAT } from "../../utils/Constant";
import { formatDateTime } from "../../utils/FormatUtils";
import { ConfirmReasonDialog } from "../ConfirmReasonDialog";

interface TimeEditorProps {
    mode?: "date" | "time" | "date-time";
    name: string;
    values: any;
    isEditingTime: boolean;
    editedTime: string;
    showValidationConfirm: boolean;
    showConfirmWithReason?: boolean;
    validationMessage: string;
    reasonKey?: string;
    onEdit: () => void;
    onCancel: () => void;
    onSave: (data?: any) => void;
    onConfirmValidation: () => void;
    onCancelValidation: () => void;
    onConfirmWithReason?: () => void;
    onCancelWithReason?: () => void;
    onTimeChange: (value: string) => void;
    inline?: boolean; // Hiển thị inline hay xuống hàng khi edit (default: true)
    viewInputType?: boolean;
    allowEdit?: boolean;
}

/**
 * Shared Component để hiển thị và edit indication time
 * Dùng chung cho cả khám bệnh và điều trị
 */
export const TimeEditor: React.FC<TimeEditorProps> = ({
    mode = "date-time",
    name = "date",
    values,
    isEditingTime,
    editedTime,
    showValidationConfirm,
    showConfirmWithReason,
    validationMessage,
    reasonKey = "reason",
    onEdit,
    onCancel,
    onSave,
    onConfirmValidation,
    onCancelValidation,
    onConfirmWithReason,
    onCancelWithReason,
    onTimeChange,
    inline = true,
    viewInputType: isViewInput = false,
    allowEdit = true,
}) => {
    return (
        <>
            {isEditingTime && inline ? (
                <div className="d-flex align-items-center gap-2 flex-grow-1">
                    <DatePickerCustom
                        mode={mode}
                        name={name}
                        value={editedTime}
                        setDateValue={(date) => {
                            // DatePicker trả về ISO string: "YYYY-MM-DDTHH:mm:ss"
                            if (date) {
                                onTimeChange(date); // Đã đúng format rồi, không cần convert
                            } else {
                                onTimeChange('');
                            }
                        }}
                        dateFormat="HH:mm dd/MM/yyyy"
                        placeholderText="14:02 07/10/2025"
                    />
                    <i
                        className="bi bi-check-circle text-success cursor-pointer"
                        style={{ fontSize: '1.2rem', cursor: 'pointer' }}
                        onClick={() => (onConfirmWithReason ?? onSave)?.()}
                        title="Lưu"
                    />
                    <i
                        className="bi bi-x-circle text-danger cursor-pointer"
                        style={{ fontSize: '1.2rem', cursor: 'pointer' }}
                        onClick={onCancel}
                        title="Hủy"
                    />
                </div>
            ) : !isEditingTime ? (
                <>
                    <span className="fw-light text-truncate flex-grow-1">
                            {isViewInput
                                ? <div className="position-relative">
                                    <DatePickerCustom
                                        mode={mode}
                                        name={name}
                                        value={values?.[name]}
                                        className="datepicker-readonly"
                                        disabled
                                    />
                                    {values?.[name] && allowEdit && (
                                        <i
                                            className="bi bi-pencil-square text-primary cursor-pointer ms-2"
                                            style={{ cursor: 'pointer', position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                                            onClick={onEdit}
                                            title="Chỉnh sửa"
                                        />
                                    )}
                                </div>
                                : <>
                                    {values?.[name]
                                        ? formatDateTime(values[name], APP_DATE_TIME_FORMAT)
                                        : "..."}

                                    {values?.[name] && allowEdit && (
                                        <i
                                            className="bi bi-pencil-square text-primary cursor-pointer ms-2"
                                            style={{ cursor: 'pointer' }}
                                            onClick={onEdit}
                                            title="Chỉnh sửa"
                                        />
                                    )}
                                </>
                            }
                    </span>
                </>
            ) : null}

            {/* Nếu inline=false và đang edit, hiển thị DatePicker ở hàng riêng */}
            {isEditingTime && !inline && (
                <div className="d-flex align-items-center gap-2 mb-3 w-100">
                    <DatePickerCustom
                        mode={mode}
                        name={name}
                        value={editedTime}
                        setDateValue={(date) => {
                            // DatePicker trả về ISO string: "YYYY-MM-DDTHH:mm:ss"
                            if (date) {
                                onTimeChange(date); // Đã đúng format rồi, không cần convert
                            } else {
                                onTimeChange('');
                            }
                        }}
                        dateFormat="HH:mm dd/MM/yyyy"
                        placeholderText="14:02 07/10/2025"
                    />
                    <i
                        className="bi bi-check-circle text-success cursor-pointer"
                        style={{ fontSize: '1.4rem', cursor: 'pointer' }}
                        onClick={() => (onConfirmWithReason ??  onSave)?.()}
                        title="Lưu"
                    />
                    <i
                        className="bi bi-x-circle text-danger cursor-pointer"
                        style={{ fontSize: '1.4rem', cursor: 'pointer' }}
                        onClick={onCancel}
                        title="Hủy"
                    />
                </div>
            )}

            <ConfirmDialog
                show={showValidationConfirm}
                onCloseClick={onCancelValidation}
                title="Xác nhận"
                message={validationMessage}
                onYesClick={onConfirmValidation}
                onCancelClick={onCancelValidation}
            />

            <ConfirmReasonDialog
                show={showConfirmWithReason!}
                close="Hủy"
                yes="Lưu"
                onYesClick={(values) => {
                    onSave(values);
                }}
                onCloseClick={() => {
                    onCancelWithReason?.();
                }}
                title="Xác nhận"
                name={reasonKey}
                label="Lý do chỉnh sửa"
            />
        </>
    );
};
